import { nomeTime } from '../data/copaMundo2026';
import {
  JOGOS_MATA_MATA,
  NOME_FASE,
  type JogoMataMata,
} from '../data/mataMataCopa2026';
import {
  calcularClassificacaoGrupo,
  grupoEstaCompleto,
  placarValido,
  vencedorRealMataMata,
  type PlacaresMap,
  type VencedoresPenaltisMMMap,
} from './copaCalculo';

export interface OpcoesResolverMataMata {
  placares: PlacaresMap;
  melhoresTerceiros: Record<number, string>;
  cruzamentos16?: Record<string, { ladoA?: string; ladoB?: string }>;
  vencedoresPenaltis?: VencedoresPenaltisMMMap;
}

export type LadoResolvidoMM = { timeId: string | null; rotulo: string };

/** Resolve o time de um lado do mata-mata a partir de placares (reais ou palpite). */
export function resolverLadoMataMataDePlacares(
  jogoId: string,
  qualLado: 'A' | 'B',
  opcoes: OpcoesResolverMataMata,
  resolverFn: (
    jogoId: string,
    qualLado: 'A' | 'B',
    opcoes: OpcoesResolverMataMata
  ) => LadoResolvidoMM = resolverLadoMataMataDePlacares
): LadoResolvidoMM {
  const { placares, melhoresTerceiros, cruzamentos16 = {}, vencedoresPenaltis = {} } =
    opcoes;
  const jogo = JOGOS_MATA_MATA.find((j) => j.id === jogoId);
  if (!jogo) return { timeId: null, rotulo: '—' };
  const lado = qualLado === 'A' ? jogo.ladoA : jogo.ladoB;

  if (jogo.fase === '16avos') {
    const over = cruzamentos16[jogoId];
    const overrideId = qualLado === 'A' ? over?.ladoA : over?.ladoB;
    if (overrideId) {
      return { timeId: overrideId, rotulo: nomeTime(overrideId) };
    }
  }

  if (lado.tipo === 'pos-grupo') {
    if (!grupoEstaCompleto(lado.grupo, placares)) {
      return {
        timeId: null,
        rotulo: `${lado.posicao}º do Grupo ${lado.grupo}`,
      };
    }
    const cls = calcularClassificacaoGrupo(lado.grupo, placares);
    const time = cls[lado.posicao - 1];
    return time
      ? { timeId: time.timeId, rotulo: nomeTime(time.timeId) }
      : { timeId: null, rotulo: `${lado.posicao}º do Grupo ${lado.grupo}` };
  }

  if (lado.tipo === 'melhor-3o') {
    const tId = melhoresTerceiros[lado.slot];
    if (tId) return { timeId: tId, rotulo: nomeTime(tId) };
    return { timeId: null, rotulo: `Melhor 3º #${lado.slot}` };
  }

  const jogoRef = JOGOS_MATA_MATA.find((j) => j.id === lado.jogoId);
  const placarAnterior = placares[lado.jogoId];
  if (jogoRef && placarValido(placarAnterior)) {
    const ladoAResolvido = resolverFn(lado.jogoId, 'A', opcoes, resolverFn);
    const ladoBResolvido = resolverFn(lado.jogoId, 'B', opcoes, resolverFn);
    if (ladoAResolvido.timeId && ladoBResolvido.timeId) {
      const vencedor = vencedorRealMataMata(
        placarAnterior,
        vencedoresPenaltis[lado.jogoId]
      );
      if (lado.tipo === 'vencedor') {
        if (vencedor === 'mandante') return ladoAResolvido;
        if (vencedor === 'visitante') return ladoBResolvido;
      } else {
        if (vencedor === 'mandante') return ladoBResolvido;
        if (vencedor === 'visitante') return ladoAResolvido;
      }
    }
  }

  const labelFase = jogoRef ? NOME_FASE[jogoRef.fase] : 'jogo anterior';
  const labelOrdem = jogoRef ? ` (jogo ${jogoRef.ordem})` : '';
  return {
    timeId: null,
    rotulo:
      lado.tipo === 'vencedor'
        ? `Vencedor: ${labelFase}${labelOrdem}`
        : `Perdedor: ${labelFase}${labelOrdem}`,
  };
}
